import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from "react";
import { Popup } from "@/components/common/Popup";
import { OrderInfo } from "@/components/order/OrderInfo";
import { BookingInfo } from "@/components/booking/BookingInfo";
import { CheckCircle, Plus, ArrowDownNarrowWide, ArrowUpWideNarrow, ArrowBigLeft, ArrowBigRight, ReceiptRussianRuble } from "lucide-react";
import { OrderInfoSchema, OrderSchema } from "@/components/order/OrderSchema";
import { BookingInfoSchema } from "@/components/booking/BookingSchema";
import React from "react";
import { Dropdown } from "@/components/ui/select";
import { DatePickerInput } from "@/components/ui/date";
import { useApiFetch } from '@/helper/apiFetch';
import { useTranslate } from '@/helper/translate';

type Lookup = { id: string; name: string, color: string, loopLength?: number };


// Component
export function PlaylistPage() {
  const [searchParams] = useSearchParams();
  const apiFetch = useApiFetch();
  const { translate } = useTranslate();

  const now = new Date();
  const dragItemId = React.useRef<string | null>(null);
  var dragOverItem = React.useRef<HTMLTableRowElement | null>(null);
  const [orders, setOrders] = useState<OrderInfoSchema[]>([]);
  //const [bookings, setBookings] = useState<BookingInfoSchema[]>([]);
  const [editOrderId, setEditOrder] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [dragContainer, setDragContainer] = useState<Lookup>();
  const [selectedSite, setSelectedSite] = useState<string>(searchParams.get('siteId') ?? "");
  const [editBooking, setEditBooking] = useState<BookingInfoSchema | null>(null);
  const [containers, setContainers] = useState<Lookup[]>([]);
  const [sites, setSites] = useState<Lookup[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const rawDateParam = searchParams.get("date");

  const [currentDate, setCurrentDate] = useState(rawDateParam ? new Date(decodeURIComponent(rawDateParam)) : new Date(now.getFullYear(), now.getMonth(), now.getDate()));

  const colHeaders = [
    translate("playlist.customer"),
    translate("playlist.name"),
    translate("playlist.orderBegin"),
    translate("playlist.orderEnd"),
    translate("playlist.rotation"),
    translate("playlist.duration"),
    translate("playlist.durationBooked"),
    translate("playlist.bookings"),
  ];

  const handleSiteChange = (siteId: string) => {
    setContainers([]);
    setRefreshCount(0);
    setSelectedSite(siteId);

  }

  const dragStart = (e: React.DragEvent<HTMLTableRowElement>) => {
    dragItemId.current = e.currentTarget.id;
    //setDragContainer(container);

  }
  const dragEnter = (e: React.DragEvent<HTMLTableRowElement>, container: Lookup, replaceId: string | null) => {
    setDragContainer(container);
    dragOverItem.current = e.currentTarget;

    document.querySelectorAll("tr").forEach((el) => {
      el.classList.remove("border-t-[50px]");
      el.classList.remove("border-b-[50px]");
      el.classList.remove("border-blue-400");
    });

    if (replaceId == null) {
      e.currentTarget.classList.add("border-b-[50px]");
      e.currentTarget.classList.add("border-gray-50");
      e.preventDefault();//set allowed cursor
    }
    else if (replaceId != dragItemId.current /*&& container === dragContainer*/) {
      e.currentTarget.classList.add("border-t-[50px]");
      e.currentTarget.classList.add("border-gray-50");
      e.preventDefault();//set allowed cursor
    }
  }

  const dragEnd = (e: React.DragEvent<HTMLTableRowElement>, orderId: string) => {

    var position = 0;

    if (dragOverItem.current)
      position = parseInt(dragOverItem.current.getAttribute("data-orderindex") ?? "0");

    if (!dragContainer)
      return;

    if (orderId && dragItemId.current) {


      apiFetch(`/App/UpdateOrderIndex?orderId=${orderId}&container=${dragContainer.id}&position=${position}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch containers");
          setRefreshCount(refreshCount + 1);


          dragItemId.current = null;
          dragOverItem.current = null;

          document.querySelectorAll("tr").forEach((el) => {
            el.classList.remove("border-t-[50px]");
            el.classList.remove("border-b-[50px]");
            el.classList.remove("border-gray-50");
          });
        })
        .catch((err) => {
          console.error(err);
        });


    }
    else {
      dragItemId.current = null;
      dragOverItem.current = null;

      document.querySelectorAll("tr").forEach((el) => {
        el.classList.remove("border-t-[50px]");
        el.classList.remove("border-b-[50px]");
        el.classList.remove("border-gray-50");
      });
    }




  }

  function getOrderLength(): number {
    const length = orders.filter(s => new Date(s.orderBegin) <= currentDate && new Date(s.orderEnd) >= currentDate)
      .reduce((sum, s) => sum + (s.numberOfLoops * s.orderLength), 0);

    return length;

  }

  function getBookingLength(orderId?: string): number {
    let l = 0;
    orders.filter(s => s.orderId == (orderId ?? s.orderId) && new Date(s.orderBegin) <= currentDate && new Date(s.orderEnd) >= currentDate).map(order => {
      const bookings = order.bookings.filter(s => new Date(s.bookingBegin) <= currentDate && new Date(s.bookingEnd) >= currentDate);
      if (order.rotation == "S")
        l += bookings.length > 0 ? bookings[0].length : 0;
      else if (order.rotation == "M" && bookings.length == 1)
        l += bookings[0].length * 2;
      else if (order.rotation == "M" && bookings.length >= 2)//should never be more than 2
        l += bookings[0].length + bookings[1].length;
      else if (order.rotation == "L" && bookings.length == 1)
        l += bookings[0].length * 3;
      else if (order.rotation == "L" && bookings.length >= 3)//should never be more than 3
        l += bookings[0].length + bookings[1].length + bookings[2].length;
      else if (order.rotation == "L" && bookings.length == 2)
        l += (bookings[0].length + bookings[1].length) / 2.0 * 3;//avg
    });


    return Math.round(l*100)/100;

  }
function updateOrder(order: string|null) {

    if (order) {
      //plug into order
      
      const { orderName, orderBegin, orderEnd, orderLength, rotation } = JSON.parse(order);
setOrders(prev => {
    const idx = prev.findIndex(o => o.orderId === editOrderId);
    if (idx === -1) return prev; // nothing to do

    const next = [...prev];
    next[idx] = { ...next[idx], orderName: orderName, orderBegin: orderBegin, orderEnd: orderEnd, orderLength: orderLength,  rotation: rotation==1?"L":rotation==2?"M":"S"  };
    return next;  
  });


    }

    setEditOrder(null);

}

function updateBooking(booking?: BookingInfoSchema) {
  if (!booking) {
    setEditBooking(null);
    return;
  }

  setOrders(prev => {
    const orderIndex = prev.findIndex(o => o.orderId === booking.orderId);
    if (orderIndex === -1) return prev; // no such order

    const order = prev[orderIndex];
    const bookings = order.bookings ?? []; // guard if it could be undefined/null

    const bookingIndex = bookings.findIndex(b => b.bookingId === booking.bookingId);
    if (bookingIndex === -1) return prev; // no such booking in that order

    // Create new arrays/objects (immutability)
    const next = [...prev];
    const nextBookings = [...bookings];
    nextBookings[bookingIndex] = booking; // replace

    next[orderIndex] = {
      ...order,
      bookings: nextBookings, // keep as array!
    };

    return next;
  });

  setEditBooking(null);
}


  const siteLoopLength = (sites.find(s => s.id == selectedSite)?.loopLength ?? 0) * 3;



  useEffect(() => {

    apiFetch(`/App/GetSiteLookup`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch sites");
        return res.json();
      })
      .then((data) => {
        setSites(data);
        if (data.length > 0 && (selectedSite == null || selectedSite == ""))
          setSelectedSite(data[0].id);
      })
      .catch((err) => {
        console.error(err);
      });


  }, []);


  useEffect(() => {
    {


      if (selectedSite && selectedSite != "") {

        apiFetch(`/App/GetContainerLookup?id=${selectedSite}`)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch containers");
            return res.json();
          })
          .then((data) => {
            setContainers(data);

          })
          .catch((err) => {
            console.error(err);
          });

      }
    }
  }, [selectedSite]);


  useEffect(() => {
    {
      if (selectedSite && selectedSite != "" && containers.length > 0) {

        refreshCount == 0 &&  setIsLoaded(false);

        apiFetch(`/App/GetPlaylist?siteId=${selectedSite}&date=${currentDate.toDateString()}`)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch playlist");
            return res.json();
          })
          .then((data) => {
            setOrders(data.orders);
            setIsLoaded(true);

          })
          .catch((err) => {
            console.error(err);
          });

      }
    }
  }, [containers, selectedSite, currentDate, refreshCount]);


  return (
    <div className="h-[calc(100vh-60px)] flex flex-col">
      <div className="content-header flex justify-between pt-[0.6rem] pb-[0.6rem] px-8 bg-gray-100 dark:bg-zinc-900">
        <div className="flex items-center gap-8 w-[100%]">
          <div className="flex items-center gap-4 w-[50%]">
            <h1 className="font-bold text-2xl">{translate("ui.playlist")}</h1>
            {/* <Plus className="w-6 h-6 cursor-pointer" onClick={() => setEditOrder("")} /> */}

            <Dropdown
              className="w-[20rem]"
              source={sites}
              value={selectedSite}
              valueMember={"id"}
              displayMember={"name"}
              onChange={(value) => handleSiteChange(value as string)}
            />

            <DatePickerInput  setMidnight={true} value={currentDate} onChange={(value) => setCurrentDate(value as Date)}></DatePickerInput>

          </div>
          <div className="flex items-center flex-col gap-0 w-[50%]">
            <div className="w-[100%] text-sm relative pl-2">
              <div className='relative z-10'>{translate("ui.looplength")}: {siteLoopLength}s</div>
              <div className="bg-slate-300 dark:bg-zinc-500 rounded-sm absolute top-0 left-0 w-[100%]" style={{ height: "100%" }}> </div>
            </div>
            <div className="w-[100%] text-sm relative pl-2">
              <div className='relative z-10'>{translate("ui.orderlength")}: {getOrderLength()}s</div>
              <div className="bg-slate-300 dark:bg-zinc-500 rounded-sm absolute top-0 left-0" style={{ width: `${(getOrderLength() / siteLoopLength) * 100}%`, height: "100%" }}> </div>

            </div>
            <div className="w-[100%] text-sm relative pl-2">

              <div className='relative z-10'>{translate("ui.bookedlength")}: {getBookingLength()}s</div>
              <div className="bg-slate-300 dark:bg-zinc-500 rounded-sm absolute top-0 left-0" style={{ width: `${(getBookingLength() / siteLoopLength) * 100}%`, height: "100%" }}> </div>

            </div>

          </div>
        </div>
      </div>


      {!isLoaded ? (
        <div className="w-full text-center my-10">{translate("common.loading")}</div>
      ) : (

        <div className="flex-1 overflow-auto px-10">

          <div className="overflow-x-auto pb-1 my-10 border border-gray-100 dark:border-zinc-600 rounded-xl shadow-2xl">

            <div>

              <table className="table-auto w-full border-collapse">

                <thead className="border-b border-gray-300 text-sm text-gray-600 dark:text-gray-50 dark:border-zinc-900">
                  <tr>
                    {colHeaders.map((header, index) => (
                      <th key={index} className="px-2 py-2 text-left cursor-pointer bg-gray-200 dark:bg-zinc-800">
                        {header}
                      </th>
                    ))}
                  </tr>

                </thead>
                <tbody>
                  {containers.filter(container => orders.some(o => o.container === container.name)).map((container, cIndex) => (
                    <React.Fragment key={cIndex}>
                      <tr style={{ backgroundColor: container.color }} className="text-center"
                        onDragOver={(e) => { dragEnter(e, container, null) }}>

                        <td className="py-2 dark:!bg-zinc-500" colSpan={colHeaders.length}>{container.name}</td>
                      </tr>
                      {orders
                        .filter((item) => item.container === container.name)
                        //sort in DB.sort((a, b) => (a.orderIndexAct ?? 0) - (b.orderIndexAct ?? 0))
                        .map((item, index) => (
                          <tr key={item.orderId}
                            data-orderindex={index}
                            className={`border-t hover:bg-gray-100 cursor-pointer dark:bg-zinc-700 dark:hover:bg-zinc-600 ${dragOverItem.current?.id === item.orderId.toString() ? "border-t-4 border-blue-400" : ""
                              }${new Date(item.orderBegin) > currentDate ? " text-gray-500 dark:text-gray-400" : ""}`}
                            onClick={() => setEditOrder(item.orderId)}
                            id={item.orderId?.toString()}
                            draggable
                            onDragStart={(e) => { dragStart(e) }}
                            onDragOver={(e) => { dragEnter(e, container, item.orderId) }}

                            onDragEnd={(e) => { dragEnd(e, item.orderId) }}
                          >
                            <td className="px-2 py-2">{item.customer}</td>
                            <td className="px-2 py-2">{item.orderName}</td>
                            <td className="px-2 py-2 ">{new Intl.DateTimeFormat("de-DE", {day:"2-digit",month:"2-digit",year:"numeric",}).format(new Date(item.orderBegin))} </td>
                            <td className="px-2 py-2 ">{new Intl.DateTimeFormat("de-DE", {day:"2-digit",month:"2-digit",year:"numeric",}).format(new Date(item.orderEnd))} </td>
                            <td className={`px-2 py-2 text-center ${item.bookings.filter(b => b.orderId == item.orderId && new Date(b.bookingBegin) <= now).length != (item.rotation == "S" ? 1 : (item.rotation == "M" ? 2 : 3)) ? "bg-gray-100 dark:bg-zinc-600" : ""
                              }`}>{item.rotation}</td>
                            <td className="px-2 py-2 text-center">{item.orderLength * item.numberOfLoops}s</td>
                            <td className="px-2 py-2 text-center">{getBookingLength(item.orderId)}s</td>
                            <td className="px-2 py-2">{
                              item.bookings
                                //.filter((booking) => booking.orderId === item.orderId)
                                .map((booking) => (
                                  <div key={booking.bookingId} className={`flex text-xs p-1  whitespace-nowrap hover:text-gray-500 dark:hover:text-gray-300 ${new Date(booking.bookingBegin) <= currentDate && new Date(booking.bookingEnd) >= currentDate ? "bg-teal-100 dark:bg-teal-600" : "text-gray-500 dark:text-gray-400"}`} onClick={(e) => { setEditBooking(booking); e.stopPropagation(); }}>
                                    {new Intl.DateTimeFormat("de-DE", { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(booking.bookingBegin))} -
                                    {new Intl.DateTimeFormat("de-DE", { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(booking.bookingEnd))} {booking.spotName}
                                    {booking.statusId == 3 ? <span className="text-green-600 ml-1"><CheckCircle className="w-5 h-5 text-green-600 bg-white rounded-full p-0.5" /></span> : <span className="text-red-600 ml-1">🚫</span>}
                                  </div>
                                ))
                            }</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>

              </table></div>

          </div>
        </div>)}
      <Popup
        isOpen={editBooking !== null}
        onClose={() => setEditBooking(null)}
        title={translate("ui.bookinginfo")}
         className="w-[800px]"
      >
        <BookingInfo onDeleted={() => { }} onClose={(booking?: BookingInfoSchema) => updateBooking(booking)} booking={editBooking as BookingInfoSchema} />
      </Popup>


      <Popup
        isOpen={editOrderId !== null}
        onClose={() => setEditOrder(null)}
        title={translate("ui.orderinfo")}
         className="w-[800px]"
      >
        <OrderInfo onClose={(order: string|null) => updateOrder(order)} Id={editOrderId ?? ""} SiteId={selectedSite} />

      </Popup>
    </div>
  );
}
